import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { 
  User, Camera, Save, ArrowLeft, MapPin, Briefcase, GraduationCap, 
  Award, Globe, Linkedin, Phone, Mail, Eye, EyeOff, Plus, X,
  FileText, Shield, Link, Clock, DollarSign, Building2, CheckCircle, ShieldCheck, Trash2
} from 'lucide-react';
import { api } from '../App';
import { toast } from 'sonner';
import Footer from '../components/Footer';

const AVAILABILITY_OPTIONS = [
  { value: 'immediate', label: 'Immediately available' },
  { value: '2_weeks', label: 'Available in 2 weeks' },
  { value: '1_month', label: 'Available in 1 month' },
  { value: 'exploring', label: 'Just exploring' }
];

const COMMON_INDUSTRIES = [
  'Technology', 'Healthcare', 'Finance', 'Education', 'Retail', 
  'Manufacturing', 'Consulting', 'Marketing', 'Real Estate', 'Non-profit'
];

export default function Profile({ user }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backgroundCheck, setBackgroundCheck] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [newIndustry, setNewIndustry] = useState('');
  const [newAchievement, setNewAchievement] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newLanguage, setNewLanguage] = useState('');
  const [newPortfolioTitle, setNewPortfolioTitle] = useState('');
  const [newPortfolioUrl, setNewPortfolioUrl] = useState('');
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    loadProfile();
    loadBackgroundCheck();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await api.get('/profile/me');
      setProfile(res.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const loadBackgroundCheck = async () => {
    try {
      const res = await api.get('/background-checks/my-checks');
      const completedCheck = res.data.find(c => c.status === 'completed');
      if (completedCheck) {
        setBackgroundCheck(completedCheck);
      }
    } catch (error) {
      // Silently fail - background check is optional
    }
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await api.put('/profile/me', {
        headline: profile.headline,
        about_me: profile.about_me,
        location: profile.location,
        phone: profile.phone,
        linkedin: profile.linkedin,
        website: profile.website,
        years_experience: profile.years_experience,
        desired_salary: profile.desired_salary,
        desired_roles: profile.desired_roles,
        industries: profile.industries,
        availability: profile.availability,
        open_to_remote: profile.open_to_remote,
        open_to_relocation: profile.open_to_relocation,
        achievements: profile.achievements,
        certifications: profile.certifications,
        languages: profile.languages,
        portfolio_links: profile.portfolio_links,
        video_intro_url: profile.video_intro_url,
        is_public: profile.is_public
      });
      toast.success('Profile saved successfully!');
    } catch (error) {
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const deleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      toast.error('Type DELETE to confirm account deletion.');
      return;
    }
    setDeletingAccount(true);
    try {
      await api.delete('/auth/account', {
        data: { password: deletePassword, confirmation: deleteConfirmation },
      });
      localStorage.removeItem('token');
      localStorage.removeItem('role');
      window.location.replace('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Account deletion failed');
    } finally {
      setDeletingAccount(false);
    }
  };

  const handlePictureUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await api.post('/profile/picture', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Reload profile to get new picture
      loadProfile();
      toast.success('Profile picture updated!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload picture');
    }
  };

  const addToList = (field, value, setter) => {
    if (value && !profile[field].includes(value)) {
      setProfile(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
      setter('');
    }
  };

  const removeFromList = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: prev[field].filter(item => item !== value)
    }));
  };

  const addPortfolioLink = () => {
    if (newPortfolioTitle && newPortfolioUrl) {
      setProfile(prev => ({
        ...prev,
        portfolio_links: [...prev.portfolio_links, { title: newPortfolioTitle, url: newPortfolioUrl }]
      }));
      setNewPortfolioTitle('');
      setNewPortfolioUrl('');
    }
  };

  const removePortfolioLink = (index) => {
    setProfile(prev => ({
      ...prev,
      portfolio_links: prev.portfolio_links.filter((_, i) => i !== index)
    }));
  };

  const calculateProfileCompleteness = () => {
    if (!profile) return 0;
    let score = 0;
    const checks = [
      profile.profile_picture,
      profile.headline,
      profile.about_me && profile.about_me.length > 50,
      profile.location,
      profile.years_experience > 0,
      profile.desired_roles?.length > 0,
      profile.industries?.length > 0,
      profile.achievements?.length > 0,
      profile.resume_attached
    ];
    checks.forEach(check => { if (check) score += 11.1; });
    return Math.round(score);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-white hover:bg-white/20" data-testid="back-btn">
                <ArrowLeft size={20} />
              </Button>
              <h1 className="text-2xl font-heading font-bold">My Profile</h1>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1 text-sm">
                <Eye size={14} />
                {profile?.profile_views || 0} views
              </div>
              <Button onClick={saveProfile} disabled={saving} className="bg-white text-blue-600 hover:bg-blue-50" data-testid="save-profile-btn">
                <Save size={16} className="mr-2" />
                {saving ? 'Saving...' : 'Save Profile'}
              </Button>
            </div>
          </div>

          {/* Profile Completeness */}
          <div className="mt-6 bg-white/10 rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm">Profile Completeness</span>
              <span className="font-bold">{calculateProfileCompleteness()}%</span>
            </div>
            <Progress value={calculateProfileCompleteness()} className="h-2 bg-white/30" />
            {calculateProfileCompleteness() < 100 && (
              <p className="text-xs mt-2 text-blue-100">Complete your profile to attract more companies!</p>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left Column - Profile Card */}
          <div className="space-y-6">
            {/* Profile Picture & Basic Info */}
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <div className="relative inline-block mb-4">
                    <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 mx-auto border-4 border-white shadow-lg">
                      {profile?.profile_picture ? (
                        <img src={profile.profile_picture} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-500 to-indigo-500 text-white text-4xl font-bold">
                          {profile?.full_name?.charAt(0) || 'U'}
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full shadow-lg hover:bg-blue-700"
                      data-testid="upload-picture-btn"
                    >
                      <Camera size={16} />
                    </button>
                    <input 
                      ref={fileInputRef}
                      type="file" 
                      accept="image/*" 
                      onChange={handlePictureUpload}
                      className="hidden"
                    />
                  </div>
                  
                  <h2 className="text-xl font-bold">{profile?.full_name}</h2>
                  <p className="text-muted-foreground text-sm">{profile?.email}</p>
                  
                  {/* Background Check Verified Badge */}
                  {backgroundCheck && (
                    <div className="mt-2 inline-flex items-center gap-1.5 bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm font-medium" data-testid="bg-check-badge">
                      <ShieldCheck size={16} className="text-green-600" />
                      Background Verified
                    </div>
                  )}
                  
                  {profile?.headline && (
                    <p className="mt-2 text-sm font-medium">{profile.headline}</p>
                  )}
                  
                  {profile?.location && (
                    <p className="text-sm text-muted-foreground flex items-center justify-center gap-1 mt-2">
                      <MapPin size={14} /> {profile.location}
                    </p>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{profile?.years_experience || 0}</p>
                    <p className="text-xs text-muted-foreground">Years Exp.</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-green-600">{profile?.achievements?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Achievements</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-purple-600">{profile?.certifications?.length || 0}</p>
                    <p className="text-xs text-muted-foreground">Certifications</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Resume Status */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText size={18} /> Resume
                </CardTitle>
              </CardHeader>
              <CardContent>
                {profile?.resume_attached ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle size={16} />
                      <span className="font-medium">Resume Attached</span>
                    </div>
                    {profile?.resume_data && (
                      <div className="text-sm text-muted-foreground">
                        <p>{profile.resume_data.experience_count} experiences</p>
                        <p>{profile.resume_data.education_count} education entries</p>
                        <p>{profile.resume_data.skills?.length || 0} skills</p>
                      </div>
                    )}
                    <Button variant="outline" size="sm" onClick={() => navigate('/resume-builder')} className="w-full mt-2">
                      Edit Resume
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <FileText className="mx-auto text-gray-400 mb-2" size={32} />
                    <p className="text-sm text-muted-foreground mb-3">No resume attached yet</p>
                    <Button size="sm" onClick={() => navigate('/resume-builder')} data-testid="create-resume-btn">
                      Create Resume
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Visibility Settings */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Profile Visibility</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {profile?.is_public ? <Eye size={16} /> : <EyeOff size={16} />}
                    <span className="text-sm">Public Profile</span>
                  </div>
                  <Switch 
                    checked={profile?.is_public} 
                    onCheckedChange={(checked) => setProfile(prev => ({ ...prev, is_public: checked }))}
                    data-testid="visibility-toggle"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  {profile?.is_public 
                    ? "Companies can discover your profile" 
                    : "Only you can see your profile"}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Details */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="about" className="w-full">
              <TabsList className="grid grid-cols-4 w-full mb-6">
                <TabsTrigger value="about">About</TabsTrigger>
                <TabsTrigger value="experience">Experience</TabsTrigger>
                <TabsTrigger value="preferences">Preferences</TabsTrigger>
                <TabsTrigger value="portfolio">Portfolio</TabsTrigger>
              </TabsList>

              {/* About Tab */}
              <TabsContent value="about" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Professional Headline</CardTitle>
                    <CardDescription>A brief title that describes who you are</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Input 
                      value={profile?.headline || ''} 
                      onChange={(e) => setProfile(prev => ({ ...prev, headline: e.target.value }))}
                      placeholder="e.g., Senior Software Engineer | 10+ Years Experience"
                      data-testid="input-headline"
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">About Me</CardTitle>
                    <CardDescription>Tell companies what makes you unique</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Textarea 
                      value={profile?.about_me || ''} 
                      onChange={(e) => setProfile(prev => ({ ...prev, about_me: e.target.value }))}
                      placeholder="Share your story, what drives you, and what you're passionate about..."
                      rows={6}
                      data-testid="input-about-me"
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      {profile?.about_me?.length || 0}/500 characters
                    </p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Contact Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-2"><MapPin size={14} /> Location</Label>
                        <Input 
                          value={profile?.location || ''} 
                          onChange={(e) => setProfile(prev => ({ ...prev, location: e.target.value }))}
                          placeholder="City, State/Country"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2"><Phone size={14} /> Phone</Label>
                        <Input 
                          value={profile?.phone || ''} 
                          onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+1 (555) 000-0000"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2"><Linkedin size={14} /> LinkedIn</Label>
                        <Input 
                          value={profile?.linkedin || ''} 
                          onChange={(e) => setProfile(prev => ({ ...prev, linkedin: e.target.value }))}
                          placeholder="linkedin.com/in/yourprofile"
                        />
                      </div>
                      <div>
                        <Label className="flex items-center gap-2"><Globe size={14} /> Website</Label>
                        <Input 
                          value={profile?.website || ''} 
                          onChange={(e) => setProfile(prev => ({ ...prev, website: e.target.value }))}
                          placeholder="yourwebsite.com"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Experience Tab */}
              <TabsContent value="experience" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Briefcase size={18} /> Years of Experience
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Input 
                      type="number"
                      min="0"
                      max="50"
                      value={profile?.years_experience || 0} 
                      onChange={(e) => setProfile(prev => ({ ...prev, years_experience: parseInt(e.target.value) || 0 }))}
                      data-testid="input-years-experience"
                    />
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Award size={18} /> Achievements
                    </CardTitle>
                    <CardDescription>Highlight your key accomplishments</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {profile?.achievements?.map((achievement, index) => (
                        <Badge key={index} variant="secondary" className="py-1 px-3">
                          {achievement}
                          <button onClick={() => removeFromList('achievements', achievement)} className="ml-2 text-red-500">
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input 
                        value={newAchievement}
                        onChange={(e) => setNewAchievement(e.target.value)}
                        placeholder="e.g., Increased sales by 150%"
                        onKeyPress={(e) => e.key === 'Enter' && addToList('achievements', newAchievement, setNewAchievement)}
                      />
                      <Button variant="outline" onClick={() => addToList('achievements', newAchievement, setNewAchievement)}>
                        <Plus size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <GraduationCap size={18} /> Certifications
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {profile?.certifications?.map((cert, index) => (
                        <Badge key={index} variant="outline" className="py-1 px-3">
                          {cert}
                          <button onClick={() => removeFromList('certifications', cert)} className="ml-2 text-red-500">
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input 
                        value={newCertification}
                        onChange={(e) => setNewCertification(e.target.value)}
                        placeholder="e.g., AWS Solutions Architect"
                        onKeyPress={(e) => e.key === 'Enter' && addToList('certifications', newCertification, setNewCertification)}
                      />
                      <Button variant="outline" onClick={() => addToList('certifications', newCertification, setNewCertification)}>
                        <Plus size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Languages</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {profile?.languages?.map((lang, index) => (
                        <Badge key={index} className="py-1 px-3">
                          {lang}
                          <button onClick={() => removeFromList('languages', lang)} className="ml-2">
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input 
                        value={newLanguage}
                        onChange={(e) => setNewLanguage(e.target.value)}
                        placeholder="e.g., English, Spanish"
                        onKeyPress={(e) => e.key === 'Enter' && addToList('languages', newLanguage, setNewLanguage)}
                      />
                      <Button variant="outline" onClick={() => addToList('languages', newLanguage, setNewLanguage)}>
                        <Plus size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Preferences Tab */}
              <TabsContent value="preferences" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Briefcase size={18} /> Desired Roles
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {profile?.desired_roles?.map((role, index) => (
                        <Badge key={index} variant="secondary" className="py-1 px-3">
                          {role}
                          <button onClick={() => removeFromList('desired_roles', role)} className="ml-2 text-red-500">
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input 
                        value={newRole}
                        onChange={(e) => setNewRole(e.target.value)}
                        placeholder="e.g., Product Manager, Data Analyst"
                        onKeyPress={(e) => e.key === 'Enter' && addToList('desired_roles', newRole, setNewRole)}
                      />
                      <Button variant="outline" onClick={() => addToList('desired_roles', newRole, setNewRole)}>
                        <Plus size={16} />
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 size={18} /> Industries
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {profile?.industries?.map((industry, index) => (
                        <Badge key={index} variant="outline" className="py-1 px-3">
                          {industry}
                          <button onClick={() => removeFromList('industries', industry)} className="ml-2 text-red-500">
                            <X size={12} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Select value={newIndustry} onValueChange={(v) => { addToList('industries', v, setNewIndustry); }}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select industry" />
                        </SelectTrigger>
                        <SelectContent>
                          {COMMON_INDUSTRIES.map(ind => (
                            <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Clock size={18} /> Availability
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <Select 
                      value={profile?.availability || 'immediate'} 
                      onValueChange={(v) => setProfile(prev => ({ ...prev, availability: v }))}
                    >
                      <SelectTrigger data-testid="availability-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABILITY_OPTIONS.map(opt => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex items-center justify-between">
                      <span className="text-sm">Open to remote work</span>
                      <Switch 
                        checked={profile?.open_to_remote} 
                        onCheckedChange={(checked) => setProfile(prev => ({ ...prev, open_to_remote: checked }))}
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <span className="text-sm">Open to relocation</span>
                      <Switch 
                        checked={profile?.open_to_relocation} 
                        onCheckedChange={(checked) => setProfile(prev => ({ ...prev, open_to_relocation: checked }))}
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <DollarSign size={18} /> Desired Salary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Input 
                      value={profile?.desired_salary || ''} 
                      onChange={(e) => setProfile(prev => ({ ...prev, desired_salary: e.target.value }))}
                      placeholder="e.g., $80,000 - $100,000"
                    />
                    <p className="text-xs text-muted-foreground mt-2">This is only visible to companies you apply to</p>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Portfolio Tab */}
              <TabsContent value="portfolio" className="space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Link size={18} /> Portfolio Links
                    </CardTitle>
                    <CardDescription>Add links to your work, projects, or GitHub</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3 mb-4">
                      {profile?.portfolio_links?.map((link, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                          <Globe size={16} className="text-blue-500" />
                          <div className="flex-1">
                            <p className="font-medium text-sm">{link.title}</p>
                            <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                              {link.url}
                            </a>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => removePortfolioLink(index)}>
                            <X size={14} className="text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="grid md:grid-cols-2 gap-3">
                      <Input 
                        value={newPortfolioTitle}
                        onChange={(e) => setNewPortfolioTitle(e.target.value)}
                        placeholder="Title (e.g., GitHub)"
                      />
                      <Input 
                        value={newPortfolioUrl}
                        onChange={(e) => setNewPortfolioUrl(e.target.value)}
                        placeholder="URL"
                      />
                    </div>
                    <Button variant="outline" onClick={addPortfolioLink} className="mt-3 w-full">
                      <Plus size={16} className="mr-2" /> Add Link
                    </Button>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Video Introduction</CardTitle>
                    <CardDescription>Add a link to a video where you introduce yourself</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Input 
                      value={profile?.video_intro_url || ''} 
                      onChange={(e) => setProfile(prev => ({ ...prev, video_intro_url: e.target.value }))}
                      placeholder="YouTube or Loom video URL"
                    />
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {user?.role !== 'admin' && (
              <Card className="mt-6 border-red-300">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg text-red-700">
                    <Trash2 size={18} /> Delete Account
                  </CardTitle>
                  <CardDescription>
                    Permanently removes your account, owned content, interview data, and active sessions. Active subscriptions are cancelled first.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {!showDeleteAccount ? (
                    <Button variant="destructive" onClick={() => setShowDeleteAccount(true)} data-testid="open-delete-account-btn">
                      Delete My Account
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="delete-account-password">Current Password</Label>
                        <Input
                          id="delete-account-password"
                          type="password"
                          autoComplete="current-password"
                          value={deletePassword}
                          onChange={(e) => setDeletePassword(e.target.value)}
                          data-testid="delete-account-password"
                        />
                      </div>
                      <div>
                        <Label htmlFor="delete-account-confirmation">Type DELETE to confirm</Label>
                        <Input
                          id="delete-account-confirmation"
                          value={deleteConfirmation}
                          onChange={(e) => setDeleteConfirmation(e.target.value)}
                          data-testid="delete-account-confirmation"
                        />
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <Button
                          variant="destructive"
                          onClick={deleteAccount}
                          disabled={deletingAccount || !deletePassword || deleteConfirmation !== 'DELETE'}
                          data-testid="confirm-delete-account-btn"
                        >
                          {deletingAccount ? 'Deleting...' : 'Permanently Delete Account'}
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowDeleteAccount(false)} disabled={deletingAccount}>
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
